<script setup lang="ts">
import type { Time } from '@internationalized/date';
import type { Temporal } from 'temporal-polyfill';
import type { DatetimeFieldOptions, FilterInputGroupProps, SegmentedFieldFilter } from '#layers/data-model/public';
import type { DatetimeFilterValue } from './filter-value';
import { computed, ref, useTemplateRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { FilterOperator } from '#layers/data-model/public';
import { injectFilterInputContext } from '#layers/filters/components/Filter/FilterInputContext';
import { DateTimePickerRangeBase, DateTimePickerTriggerButton, formatDateRangeSummary, pickerPopoverContent, pickerPopoverUi, plainDateRangeAdapter } from '#layers/ui/public';
import { buildDatetimeFilterContext, createDatetimeRangeAdapter, datetimeRangeTimePart, formatDatetimeFilterValue, getDatetimeRangeModel, isDateDatetimeFilter, isTimeDatetimeFilter, isTimestampValueCompatibleWithBacking, setDatetimeRangeBoundary, withDatetimeRangeTime } from './filter-value';
import DatetimeFilterValueEditor from './FilterValueEditor.vue';

defineOptions({ name: 'DatetimeFilterInputGroup' });

const props = defineProps<FilterInputGroupProps<DatetimeFieldOptions>>();

const emit = defineEmits<{
	remove: [];
}>();

const model = defineModel<SegmentedFieldFilter | null>({ required: true });

const filterInputContext = injectFilterInputContext();
const { t, locale } = useI18n();

const isRangeOperator = computed(() => model.value?.operator === FilterOperator.BETWEEN || model.value?.operator === FilterOperator.NBETWEEN);
const isDate = computed(() => isDateDatetimeFilter(props.primitiveType));
const isTime = computed(() => isTimeDatetimeFilter(props.primitiveType));
const isInteractionDisabled = computed(() => props.disabled || props.readonly);
const delegatedInputGroupRef = useTemplateRef<{ focusFirstInput: () => void }>('delegatedInputGroupRef');
const rangePickerRef = useTemplateRef<{ focusFirstInput: () => void }>('rangePickerRef');
const timeRangePopoverOpen = ref(false);
const pendingDelegatedFocus = ref(false);

const rangeValue = computed(() => getDatetimeRangeModel(model.value?.value));
const context = computed(() => buildDatetimeFilterContext(props.primitiveType, props.typeOptions));

// A `date` backing stores `Temporal.PlainDate` operands; narrow the untyped range slots for the shared picker.
const rangeStartDate = computed(() => (rangeValue.value[0] ?? null) as Temporal.PlainDate | null);
const rangeEndDate = computed(() => (rangeValue.value[1] ?? null) as Temporal.PlainDate | null);

const rangeStartDisplay = computed(() => formatDatetimeFilterValue(rangeValue.value[0], context.value, locale.value));
const rangeEndDisplay = computed(() => formatDatetimeFilterValue(rangeValue.value[1], context.value, locale.value));

const isRangeEmpty = computed(() => !rangeStartDisplay.value && !rangeEndDisplay.value);

const rangeDisplay = computed(() => formatDateRangeSummary(t, rangeStartDisplay.value, rangeEndDisplay.value, '...'));

// Shared trigger styling for both the date-range and datetime-range chips; only label/is-empty differ.
const rangeTriggerProps = {
	showIcon: false,
	showDisclosure: false,
	block: false,
	variant: 'ghost',
	size: 'xs',
	dataTestid: 'datetime-filter-range',
	class: 'max-w-56',
} as const;

function focusFirstInput() {
	if (isInteractionDisabled.value) return;

	if (!isRangeOperator.value) {
		if (!delegatedInputGroupRef.value) {
			pendingDelegatedFocus.value = true;
			return;
		}

		pendingDelegatedFocus.value = false;
		delegatedInputGroupRef.value.focusFirstInput();
		return;
	}

	if (isTime.value) {
		timeRangePopoverOpen.value = true;
		return;
	}

	rangePickerRef.value?.focusFirstInput();
}

defineExpose({ focusFirstInput });

watch(delegatedInputGroupRef, (inputGroup) => {
	if (!inputGroup || !pendingDelegatedFocus.value) return;

	focusFirstInput();
});

function updateRangeBoundary(index: 0 | 1, value: unknown) {
	if (!model.value || isInteractionDisabled.value) return;
	// Both range bounds are required: ignore attempts to clear either, so a range always keeps its bounds.
	if (isRangeOperator.value && value == null) return;
	if (!isTimestampValueCompatibleWithBacking(value, props.primitiveType)) return;
	model.value = setDatetimeRangeBoundary(model.value, index, value);
}

// Date+time backings reuse the shared range picker: the calendar drives the date span, a time input per endpoint sets the time.
const datetimeRangeAdapter = computed(() => createDatetimeRangeAdapter(context.value));
const rangeStartValue = computed(() => (rangeValue.value[0] ?? null) as DatetimeFilterValue | null);
const rangeEndValue = computed(() => (rangeValue.value[1] ?? null) as DatetimeFilterValue | null);

const startTimeModel = computed<Time | null>({
	get: () => datetimeRangeTimePart(rangeValue.value[0], context.value),
	set: (time) => {
		if (time == null) return;
		updateRangeBoundary(0, withDatetimeRangeTime(rangeValue.value[0], time, context.value));
	},
});

const endTimeModel = computed<Time | null>({
	get: () => datetimeRangeTimePart(rangeValue.value[1], context.value),
	set: (time) => {
		if (time == null) return;
		updateRangeBoundary(1, withDatetimeRangeTime(rangeValue.value[1], time, context.value));
	},
});
</script>

<template>
	<!-- Non-range operators delegate to the default group, which reads model/config from the delegate context. -->
	<component
		:is="filterInputContext.FilterInputGroup"
		v-if="!isRangeOperator"
		ref="delegatedInputGroupRef"
		v-model="model"
		@remove="emit('remove')"
	/>
	<!-- A `date` range reuses the shared range picker for the continuous-range UX; future dates stay available. -->
	<DateTimePickerRangeBase
		v-else-if="isDate"
		ref="rangePickerRef"
		:adapter="plainDateRangeAdapter"
		:start-date="rangeStartDate"
		:end-date="rangeEndDate"
		:readonly
		:disabled
		:block="false"
		:clearable="false"
		size="sm"
		@update:start-date="updateRangeBoundary(0, $event)"
		@update:end-date="updateRangeBoundary(1, $event)"
	>
		<template #trigger="{ rangeFieldDisplay, isRangeEmpty: isPickerRangeEmpty }">
			<DateTimePickerTriggerButton
				v-bind="rangeTriggerProps"
				:label="rangeFieldDisplay"
				:is-empty="isPickerRangeEmpty"
				:disabled
				:readonly
			/>
		</template>
	</DateTimePickerRangeBase>
	<!-- `time` has no calendar span, so it stays two independent time editors. -->
	<DPopover
		v-else-if="isTime"
		v-model:open="timeRangePopoverOpen"
		:disabled="isInteractionDisabled"
		:content="{ ...pickerPopoverContent, align: 'start' }"
		:ui="pickerPopoverUi"
	>
		<DateTimePickerTriggerButton
			v-bind="rangeTriggerProps"
			:label="rangeDisplay"
			:is-empty="isRangeEmpty"
			:disabled
			:readonly
		/>

		<template #content>
			<div class="flex w-max gap-4" data-testid="datetime-filter-range-panels">
				<DatetimeFilterValueEditor
					:model-value="rangeValue[0]"
					:primitive-type
					:type-options
					:disabled
					:readonly
					:show-clear="false"
					size="sm"
					:label="$t('date_range_picker.start_date')"
					@update:model-value="updateRangeBoundary(0, $event)"
				/>
				<DatetimeFilterValueEditor
					:model-value="rangeValue[1]"
					:primitive-type
					:type-options
					:disabled
					:readonly
					:show-clear="false"
					size="sm"
					:label="$t('date_range_picker.end_date')"
					@update:model-value="updateRangeBoundary(1, $event)"
				/>
			</div>
		</template>
	</DPopover>
	<!-- Date+time backings: the shared continuous-range picker, with a time input per endpoint. -->
	<DateTimePickerRangeBase
		v-else
		ref="rangePickerRef"
		:adapter="datetimeRangeAdapter"
		:start-date="rangeStartValue"
		:end-date="rangeEndValue"
		:readonly
		:disabled
		:block="false"
		:clearable="false"
		size="sm"
		@update:start-date="updateRangeBoundary(0, $event)"
		@update:end-date="updateRangeBoundary(1, $event)"
	>
		<template #trigger="{ rangeFieldDisplay, isRangeEmpty: isPickerRangeEmpty }">
			<DateTimePickerTriggerButton
				v-bind="rangeTriggerProps"
				:label="rangeFieldDisplay"
				:is-empty="isPickerRangeEmpty"
				:disabled
				:readonly
			/>
		</template>
		<template #start-extra>
			<div class="flex justify-center pt-2">
				<DInputTime v-model="startTimeModel" granularity="minute" size="sm" :disabled :readonly />
			</div>
		</template>
		<template #end-extra>
			<div class="flex justify-center pt-2">
				<DInputTime v-model="endTimeModel" granularity="minute" size="sm" :disabled :readonly />
			</div>
		</template>
	</DateTimePickerRangeBase>
</template>
